/**
 *
 * @param {*} rgbIn
 * @returns
 */
export function rgbString2hexString(rgbIn) {
    const rgb = rgbIn.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    function hex(x) {
        return (`0${Number(x).toString(16)}`).slice(-2);
    }
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
}

/**
 *
 * @param {*} hex
 * @returns
 */
export function hexToRgb(hex) {
    const r = (hex >> 16) & 0xff;
    const g = (hex >> 8) & 0xff;
    const b = hex & 0xff;
    // console.log(`hexToRgb: ${hex} = ${r},${g},${b}`);
    return [r, g, b];
}

/**
 *
 * @param {*} r
 * @param {*} g
 * @param {*} b
 * @returns
 */
export function rgbToHex(r, g, b) {
    const rPart = (r & 0xff) << 16;
    const gPart = (g & 0xff) << 8;
    const bPart = b & 0xff;
    const hex = rPart + gPart + bPart;
    // console.log(`rgbToHex: ${r},${g},${b} = ${hex}`);
    return hex;
}

/**
 *
 * @param {*} hex
 * @returns
 */
export function hexToHsl(hex) {
    const hsl = rgbToHsl(...hexToRgb(hex));
    // console.log(`hexToHsl: ${hex} = ${hsl[0]},${hsl[1]},${hsl[2]}`);
    return hsl;
}

/**
 *
 * @param {*} h
 * @param {*} s
 * @param {*} l
 * @returns
 */
export function hslToHex(h, s, l) {
    const hex = rgbToHex(...hslToRgb(h, s, l));
    // console.log(`hslToHex: ${h},${s},${l} = ${hex}`);
    return hex;
}

/**
 *
 * @param {number} rIn Range: 0-255
 * @param {number} gIn Range: 0-255
 * @param {number} bIn Range: 0-255
 * @returns { [number, number, number] } h, s, l
 *
 * rgbToHsl is taken from https://github.com/JuhQ/rgb-to-hsl
 *
 * The MIT License (MIT)
 * Copyright (c) 2016 Juha Tauriainen
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
export function rgbToHsl(rIn, gIn, bIn) {
    const r = rIn / 255;
    const g = gIn / 255;
    const b = bIn / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
            // no default
        }
        h /= 6;
    } else {
        // h and s are already 0
    }

    h *= 360;
    return [h, s, l];
}

/**
 * Convert h,s,l values to r,g,b
 * From https://github.com/o-klp/hsl_rgb_converter
 *
 * @param {number} hue Range: 0-360
 * @param {number} saturation Range: 0-1
 * @param {number} lightness Range: 0-1
 * @returns {[number, number, number]} r, g, b
 */
export function hslToRgb(hue, saturation, lightness) {
    // based on algorithm from http://en.wikipedia.org/wiki/HSL_and_HSV#Converting_to_RGB
    if (hue === undefined) {
        return [0, 0, 0];
    }

    const chroma = (1 - Math.abs((2 * lightness) - 1)) * saturation;
    let huePrime = hue / 60;
    const secondComponent = chroma * (1 - Math.abs((huePrime % 2) - 1));

    huePrime = Math.floor(huePrime);
    let red;
    let green;
    let blue;

    if (huePrime === 0) {
        red = chroma;
        green = secondComponent;
        blue = 0;
    } else if (huePrime === 1) {
        red = secondComponent;
        green = chroma;
        blue = 0;
    } else if (huePrime === 2) {
        red = 0;
        green = chroma;
        blue = secondComponent;
    } else if (huePrime === 3) {
        red = 0;
        green = secondComponent;
        blue = chroma;
    } else if (huePrime === 4) {
        red = secondComponent;
        green = 0;
        blue = chroma;
    } else if (huePrime === 5) {
        red = chroma;
        green = 0;
        blue = secondComponent;
    }

    const lightnessAdjustment = lightness - (chroma / 2);
    red += lightnessAdjustment;
    green += lightnessAdjustment;
    blue += lightnessAdjustment;

    return [Math.round(red * 255), Math.round(green * 255), Math.round(blue * 255)];
}
